import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, FormField, Input, Checkbox, ControlRow } from '@vd/ui';
import { AuthLayout } from './AuthLayout';
import { api } from '../../lib/api';
import { useAuth } from '../../features/auth/AuthProvider';

export function SignUpPage() {
  const [searchParams] = useSearchParams();
  // Invite link → /signup carries the recipient's email + invite token
  // so the new account is auto-joined to the company on submit.
  const inviteEmail = searchParams.get('email') ?? '';
  const inviteToken = searchParams.get('invite') ?? '';
  const isInviteSignup = !!inviteToken;

  const [name, setName] = useState('');
  // When invited, lock the email field — the signup endpoint requires
  // the email to match the invite, and letting users edit it just
  // creates a 403 they can't recover from on this page.
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState('');
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { refreshUser, setCurrentCompany } = useAuth();

  const subtitle = useMemo(
    () => isInviteSignup
      ? 'Set a password to finish joining your team.'
      : 'Capture, annotate, and hand off bugs to Claude Code in seconds.',
    [isInviteSignup],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accept) { setError('Please accept terms and privacy to continue.'); return; }
    if (password.length < 10) { setError('Password must be at least 10 characters.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.signUp({
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        ...(inviteToken ? { inviteToken } : {}),
      });
      // Refresh AuthProvider so the new session + companies are live.
      await refreshUser();
      // If signup auto-accepted an invite, drop the user on / where the
      // home routing logic will land them on the company's board (or
      // welcome panel if no tickets yet). Otherwise, send them through
      // onboarding to set up their own company + project.
      if (result.joinedCompanyId) {
        setCurrentCompany(result.joinedCompanyId);
        navigate('/', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={isInviteSignup ? 'Finish joining your team' : 'Create your account'}
      subtitle={subtitle}
      footer={<>Already have an account? <Link to="/signin">Sign in</Link></>}
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Full name">
          <Input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </FormField>
        <FormField
          label="Email"
          help={isInviteSignup ? 'This is the address your invite was sent to.' : undefined}
        >
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            readOnly={isInviteSignup}
            style={isInviteSignup ? { opacity: 0.7 } : undefined}
          />
        </FormField>
        <FormField label="Password" help="At least 10 characters.">
          <Input type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </FormField>
        <ControlRow>
          <Checkbox checked={accept} onChange={(e) => setAccept(e.target.checked)} />
          <span>
            I agree to the{' '}
            <a href="https://www.toado.dev/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            {' '}and{' '}
            <a href="https://www.toado.dev/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        </ControlRow>
        {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
        <Button variant="primary" size="lg" loading={submitting} type="submit">
          {isInviteSignup ? 'Create account & join' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
